<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useSourceActions } from '../useSourceActions';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const { isSubmitting, sendComplaint } = useSourceActions();

const dialogRef = ref(null);
const reason = ref('');
const comment = ref('');

const REASONS = [
  'INSULTS',
  'SPAM',
  'NOT_OUR_COMPANY',
  'CONFLICT_OF_INTEREST',
  'PERSONAL_DATA',
  'OTHER',
];

const reasons = computed(() =>
  REASONS.map(key => ({
    key,
    label: t(`MULTICHAT.SOURCE.COMPLAINT.REASONS.${key}`),
  }))
);

const selectedLabel = computed(
  () => reasons.value.find(item => item.key === reason.value)?.label || ''
);

const canSubmit = computed(
  () => !!reason.value && (reason.value !== 'OTHER' || !!comment.value.trim())
);

const open = () => {
  reason.value = '';
  comment.value = '';
  dialogRef.value?.open();
};

const submit = async () => {
  if (!canSubmit.value) return;
  try {
    await sendComplaint(props.conversationId, {
      reason: selectedLabel.value,
      comment: comment.value.trim(),
    });
    useAlert(t('MULTICHAT.SOURCE.COMPLAINT.SUCCESS'));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(t('MULTICHAT.SOURCE.COMPLAINT.ERROR'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('MULTICHAT.SOURCE.COMPLAINT.TITLE')"
    :description="t('MULTICHAT.SOURCE.COMPLAINT.DESCRIPTION')"
    :confirm-button-label="t('MULTICHAT.SOURCE.COMPLAINT.SUBMIT')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    width="md"
    @confirm="submit"
  >
    <div class="flex flex-col gap-1.5">
      <label
        v-for="item in reasons"
        :key="item.key"
        class="flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm"
        :class="
          reason === item.key
            ? 'border-n-brand bg-n-brand/5 text-n-slate-12'
            : 'border-n-weak hover:bg-n-alpha-1 text-n-slate-12'
        "
      >
        <input
          v-model="reason"
          type="radio"
          name="complaint-reason"
          :value="item.key"
          class="accent-n-brand"
        />
        {{ item.label }}
      </label>
    </div>
    <TextArea
      v-model="comment"
      :label="t('MULTICHAT.SOURCE.COMPLAINT.COMMENT')"
      :placeholder="t('MULTICHAT.SOURCE.COMPLAINT.COMMENT_PLACEHOLDER')"
      :max-length="1000"
      min-height="5rem"
    />
  </Dialog>
</template>
